import { useMemo } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, Wallet, BarChart3 } from 'lucide-react';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { ViewMode } from '@/types/finance';

const fmt = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
const fmtFull = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { color: string; name: string; value: number }[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-card p-3 text-sm shadow-lg">
        <p className="mb-2 font-semibold text-foreground">{label}</p>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-medium text-foreground">{fmt(entry.value)}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export function CashFlowPage() {
  const { clientId } = useCurrentClient();
  const { getChartData, getDashboardSummary, filters, setFilters, accounts } = useFinance();

  const chartData = useMemo(() => getChartData(), [getChartData, filters]);
  const summary = useMemo(() => getDashboardSummary(), [getDashboardSummary, filters]);

  const showPredicted = filters.viewMode !== 'realized';
  const showRealized = filters.viewMode !== 'predicted';

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 3 + i);

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para ver o fluxo de caixa.</p>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="font-brand text-2xl font-bold text-foreground">Fluxo de Caixa</h1>
          <p className="text-sm text-muted-foreground">Projetado vs Realizado</p>
        </div>
        <div className="rounded-xl border border-border bg-white p-12 text-center text-muted-foreground">
          <BarChart3 className="mx-auto mb-3 h-12 w-12 opacity-30" />
          <p className="font-medium">Cadastre contas e transações para ver o fluxo de caixa.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Fluxo de Caixa</h1>
          <p className="text-sm text-muted-foreground">Projetado vs Realizado — {filters.year}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Visualização */}
          <div className="flex gap-1 rounded-lg border bg-muted/50 p-1">
            {[
              { value: 'all' as ViewMode, label: 'Todos' },
              { value: 'realized' as ViewMode, label: 'Realizados' },
              { value: 'predicted' as ViewMode, label: 'Projetados' },
            ].map(({ value, label }) => (
              <Button
                key={value}
                variant={filters.viewMode === value ? 'default' : 'ghost'}
                size="sm"
                className="h-8"
                onClick={() => setFilters({ ...filters, viewMode: value })}
              >
                {label}
              </Button>
            ))}
          </div>
          {/* Ano */}
          <Select
            value={filters.year.toString()}
            onValueChange={(v) => {
              const { month: _m, ...rest } = filters;
              setFilters({ ...rest, year: parseInt(v), periodMode: 'monthly' });
            }}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: 'Saldo em Contas',
            value: summary.currentBalance,
            icon: Wallet,
            color: 'primary',
          },
          {
            label: 'Receitas do Período',
            value: summary.totalIncomeRealized + summary.totalIncomePredicted,
            icon: TrendingUp,
            color: 'income',
          },
          {
            label: 'Despesas do Período',
            value: summary.totalExpenseRealized + summary.totalExpensePredicted,
            icon: TrendingDown,
            color: 'expense',
          },
          {
            label: 'Saldo do Período',
            value: summary.periodBalance,
            icon: BarChart3,
            color: summary.periodBalance >= 0 ? 'income' : 'expense',
          },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 bg-${color}/10`}>
                  <Icon className={`h-5 w-5 text-${color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-lg font-bold text-${color}`}>{fmtFull(value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gráfico principal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Fluxo de Caixa {filters.year}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <ReferenceLine
                  y={0}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="5 5"
                  opacity={0.7}
                />
                <XAxis
                  dataKey="period"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={{ stroke: 'hsl(var(--border))' }}
                />
                <YAxis
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={{ stroke: 'hsl(var(--border))' }}
                  tickFormatter={fmt}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="rect"
                  formatter={(v) => <span className="text-sm text-muted-foreground">{v}</span>}
                />

                {showRealized && (
                  <Bar
                    dataKey="incomeRealized"
                    name="Receita Realizada"
                    fill="hsl(var(--income))"
                    fillOpacity={0.9}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={40}
                  />
                )}
                {showRealized && (
                  <Bar
                    dataKey="expenseRealized"
                    name="Despesa Realizada"
                    fill="hsl(var(--expense))"
                    fillOpacity={0.9}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={40}
                  />
                )}
                {showPredicted && (
                  <Bar
                    dataKey="incomePredicted"
                    name="Receita Prevista"
                    fill="hsl(var(--income))"
                    fillOpacity={0.3}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={40}
                  />
                )}
                {showPredicted && (
                  <Bar
                    dataKey="expensePredicted"
                    name="Despesa Prevista"
                    fill="hsl(var(--expense))"
                    fillOpacity={0.3}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={40}
                  />
                )}
                {showRealized && (
                  <Line
                    dataKey="balanceRealized"
                    name="Saldo Realizado"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                    type="monotone"
                  />
                )}
                {showPredicted && (
                  <Line
                    dataKey="balancePredicted"
                    name="Saldo Previsto"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    type="monotone"
                    opacity={0.6}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
